import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { Automation } from '@core/typings/ui/automation.typing';
import { TypeSafeFormGroup, TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { isEqual } from 'lodash';
import { RuleGroup } from '../automation-rule/automation-rule.component';
import { RuleAutomationService } from '../rule-automation.service';
import { ArrayHelpersService } from '@yourcause/common/utils';

@Component({
    selector: 'gc-automation-criteria',
    templateUrl: './automation-criteria.component.html',
    styleUrls: ['./automation-criteria.component.scss'],
    standalone: false
})
export class AutomationCriteriaComponent implements OnInit {
  @Input() isModalView: boolean;
  @Input() public get objects (): Automation.ObjectConfig[] {
    return this._objects;
  }
  public set objects (value: Automation.ObjectConfig[]) {
    this._objects = value;
    this.hideObjectSelector = value.length === 1;
    this.setObjectOptions();
  }

  @Input() criteriaFormGroup: TypeSafeFormGroup<RuleGroup>;
  @Input() ruleIndex: number;
  @Input()
  public get usedColumns (): string[] {
    return this._usedColumns;
  }
  public set usedColumns (value: string[]) {
    this._usedColumns = value;
    this.setObjectOptions();

    if (this.criteriaFormGroup.value.object) {
      this.handleObjectChange(this.criteriaFormGroup.value.object);
    }
  }

  @Output() onRemove = new EventEmitter();
  @Output() columnChanged = new EventEmitter<string>();

  private _objects: Automation.ObjectConfig[];
  private _usedColumns: string[] = [];
  Comparisons = Automation.Comparisons;
  objectOptions: TypeaheadSelectOption<Automation.ObjectConfig>[];
  columnOptions: TypeaheadSelectOption<Automation.ObjectColumnConfig>[];
  columnComparisonOptions: TypeaheadSelectOption[] = [];
  currentComparison: Automation.Comparisons = null;
  hideObjectSelector = false; // We will only hide this when there is only one object, meaning it't not necessary to display selector
  CriteriaComparisonTypes = Automation.CriteriaComparisonTypes;

  // return every other column EXCEPT this one
  get applicableUsedColumns () {
    return this.usedColumns
      .filter((_, index) => index !== this.ruleIndex);
  }

  constructor (
    private ruleAutomationService: RuleAutomationService,
    private arrayHelper: ArrayHelpersService
  ) { }

  ngOnInit () {
    this.setObjectOptions();
    const value = this.criteriaFormGroup.value;
    this.currentComparison = value.column ?
      value.column.comparison :
      null;

    const column: Automation.ObjectColumnConfig = value.column;

    if (column) {
      this.setColumnComparisonOptions(column);
    }
    if (value.object) {
      this.handleObjectChange(value.object);
    }
  }

  private setObjectOptions () {
    const selectedObject = this.criteriaFormGroup ?
      this.criteriaFormGroup.value.object :
      null;
    const isInvalid = selectedObject &&
      this.objects &&
      !this.objects.some(option => isEqual(option, selectedObject));

    if (isInvalid) {
      if (!this.hideObjectSelector) {
        this.criteriaFormGroup.get('object').setValue(null);
      }
      this.columnOptions = null;
      this.currentComparison = null;
    }

    const objects = this.objects?.filter(object => {
      return object.columns
        .some(column => {
          if (selectedObject && (object.key === selectedObject.key)) {
            return true;
          }

          return !this.applicableUsedColumns
            .includes(object.key + '.' + column.key);
        });
    }) ?? [];

    this.objectOptions = objects.map(object => ({
      label: object.label,
      value: object
    }));
  }

  handleObjectChange (
    object: Automation.ObjectConfig
  ) {
    if (object) {
      const formGroupValue: Automation.CriteriaFormState = this.criteriaFormGroup.value;

      this.columnOptions = this.arrayHelper.sort(object.columns
        .filter(column => {
          if (formGroupValue.column && (formGroupValue.column.key === column.key)) {
            return true;
          }
          const key = formGroupValue.object.key + '.' + column.key;

          return !this.applicableUsedColumns.includes(key);
        })
        .map(column => ({
          label: column.label,
          value: column
        })),
        'label'
      );
      const columnControl = this.criteriaFormGroup.get('column');
      if (!object.columns.includes(columnControl.value)) {
        this.currentComparison = null;
        columnControl.setValue(null);
      }
    }
  }

  handleColumnChange (
    column: Automation.ObjectColumnConfig
  ) {
    if (column) {
      const valuesArray = this.criteriaFormGroup.get('values') as UntypedFormArray;
      this.ruleAutomationService.resetValues(valuesArray);
      this.columnChanged.emit(this.criteriaFormGroup.value.object.key + '.' + column.key);
      this.setColumnComparisonOptions(column);
      this.currentComparison = column.comparison;
    }
  }

  setColumnComparisonOptions (column: Automation.ObjectColumnConfig<any>) {
    this.columnComparisonOptions = this.ruleAutomationService.getColumnComparisonOptions(column);
  }
}
